import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  DocumentTextIcon,
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  CalendarDaysIcon,
  CurrencyRupeeIcon,
  FunnelIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  useGetMySalarySlipsQuery,
  useGetMySalarySlipByIdQuery,
  useLazyDownloadMySalarySlipQuery,
} from '../../services/employeeApi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage';
import Modal from '../../components/common/Modal';
import toast from 'react-hot-toast';

const SalarySlipDownload = () => {
  const [search, setSearch] = useState('');
  const [year, setYear] = useState('');
  const [month, setMonth] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedPayrollId, setSelectedPayrollId] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  const { data, isLoading, error, refetch } = useGetMySalarySlipsQuery({
    page,
    limit: 10,
    search,
    year,
    month,
    status,
  });

  const {
    data: salarySlipData,
    isLoading: isLoadingSalarySlip,
    error: salarySlipError,
  } = useGetMySalarySlipByIdQuery(selectedPayrollId, {
    skip: !selectedPayrollId,
  });

  const [downloadSalarySlip, { isLoading: isDownloading }] = useLazyDownloadMySalarySlipQuery();

  const salarySlips = data?.data?.salarySlips || [];
  const pagination = data?.data?.pagination || {};

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const statusColors = {
    draft: 'bg-gray-100 text-gray-800',
    approved: 'bg-green-100 text-green-800',
    paid: 'bg-blue-100 text-blue-800',
    cancelled: 'bg-red-100 text-red-800',
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleFilterChange = (filterType, value) => {
    switch (filterType) {
      case 'year':
        setYear(value);
        break;
      case 'month':
        setMonth(value);
        break;
      case 'status':
        setStatus(value);
        break;
      default:
        break;
    }
    setPage(1);
  };

  const clearFilters = () => {
    setSearch('');
    setYear('');
    setMonth('');
    setStatus('');
    setPage(1);
  };

  const handleViewSalarySlip = (payrollId) => {
    setSelectedPayrollId(payrollId);
    setShowViewModal(true);
  };

  const handleDownloadSalarySlip = async (payrollId, employeeId, month, year) => {
    try {
      const response = await downloadSalarySlip(payrollId).unwrap();
      
      // Check if response is a Blob (PDF)
      if (response instanceof Blob) {
        // Create download link for PDF
        const url = window.URL.createObjectURL(response);
        const link = document.createElement('a');
        link.href = url;
        link.download = `salary-slip-${monthNames[month - 1]}-${year}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up the URL
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
        
        toast.success('Salary slip downloaded successfully!');
      } else {
        // Fallback for HTML content (if PDF generation fails)
        const blob = new Blob([response], { type: 'text/html' });
        const url = window.URL.createObjectURL(blob);
        const newWindow = window.open(url, '_blank');
        
        if (newWindow) {
          newWindow.document.title = `Salary Slip - ${monthNames[month - 1]} ${year}`;
          setTimeout(() => window.URL.revokeObjectURL(url), 1000);
        } else {
          toast.error('Please allow pop-ups for this site to view salary slips');
        }
      }
    } catch (error) {
      console.error('Error downloading salary slip:', error);
      toast.error('Failed to download salary slip');
    }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message="Failed to load salary slips" />;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div className="mb-4 sm:mb-0">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">My Salary Slips</h1>
              <p className="text-gray-600">View and download your salary slips</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="bg-white px-4 py-2 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center space-x-2">
                  <DocumentTextIcon className="h-5 w-5 text-blue-600" />
                  <span className="text-sm font-medium text-gray-900">
                    {pagination.totalRecords || 0} Salary Slips
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8">
          <div className="p-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search salary slips..."
                  value={search}
                  onChange={handleSearch}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                />
              </div>

              {/* Filter Toggle */}
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="inline-flex items-center px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  <FunnelIcon className="h-4 w-4 mr-2" />
                  Filters
                  {showFilters ? (
                    <XMarkIcon className="h-4 w-4 ml-2" />
                  ) : null}
                </button>
                {(year || month || status) && (
                  <button
                    onClick={clearFilters}
                    className="inline-flex items-center px-4 py-2.5 text-sm font-medium text-red-600 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <XMarkIcon className="h-4 w-4 mr-2" />
                    Clear
                  </button>
                )}
              </div>
            </div>

            {/* Filters */}
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-6 pt-6 border-t border-gray-200"
              >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Year
                    </label>
                    <select
                      value={year}
                      onChange={(e) => handleFilterChange('year', e.target.value)}
                      className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    >
                      <option value="">All Years</option>
                      {years.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Month
                    </label>
                    <select
                      value={month}
                      onChange={(e) => handleFilterChange('month', e.target.value)}
                      className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    >
                      <option value="">All Months</option>
                      {monthNames.map((monthName, index) => (
                        <option key={index + 1} value={index + 1}>
                          {monthName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Status
                    </label>
                    <select
                      value={status}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                      className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    >
                      <option value="">All Status</option>
                      <option value="draft">Draft</option>
                      <option value="approved">Approved</option>
                      <option value="paid">Paid</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </div>

        {/* Salary Slips Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {salarySlips.map((slip) => (
            <motion.div
              key={slip.payrollId}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-lg hover:border-gray-300 transition-all duration-200 overflow-hidden"
            >
              <div className="p-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl">
                      <DocumentTextIcon className="h-6 w-6 text-white" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {monthNames[slip.month - 1]} {slip.year}
                      </h3>
                      <p className="text-sm text-gray-500">{slip.payrollPeriod}</p>
                    </div>
                  </div>
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                      statusColors[slip.status] || 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {slip.status}
                  </span>
                </div>

                {/* Salary Details */}
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between items-center py-2">
                    <span className="text-sm font-medium text-gray-600">Gross Earnings</span>
                    <span className="text-sm font-semibold text-gray-900">
                      ₹{slip.grossEarnings?.toLocaleString('en-IN') || 0}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-sm font-medium text-gray-600">Total Deductions</span>
                    <span className="text-sm font-semibold text-red-600">
                      ₹{slip.totalDeductions?.toLocaleString('en-IN') || 0}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 pt-3 border-t border-gray-200">
                    <span className="text-base font-semibold text-gray-900">Net Salary</span>
                    <span className="text-xl font-bold text-green-600">
                      ₹{slip.netSalary?.toLocaleString('en-IN') || 0}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex space-x-3">
                  <button
                    onClick={() => handleViewSalarySlip(slip.payrollId)}
                    className="flex-1 inline-flex items-center justify-center px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                  >
                    <EyeIcon className="h-4 w-4 mr-2" />
                    View
                  </button>
                  <button
                    onClick={() => handleDownloadSalarySlip(slip.payrollId, 'EMP001', slip.month, slip.year)}
                    disabled={isDownloading}
                    className="flex-1 inline-flex items-center justify-center px-4 py-2.5 border border-transparent rounded-lg text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                    {isDownloading ? 'Downloading...' : 'Download'}
                  </button>
                </div>

                {/* Generated Date */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-xs text-gray-400 flex items-center">
                    <CalendarDaysIcon className="h-3 w-3 mr-1" />
                    Generated on {new Date(slip.generatedAt).toLocaleDateString('en-IN')}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Empty State */}
        {salarySlips.length === 0 && (
          <div className="text-center py-16">
            <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <DocumentTextIcon className="h-12 w-12 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No salary slips found</h3>
            <p className="text-gray-500 max-w-md mx-auto">
              {search || year || month || status
                ? 'Try adjusting your search or filter criteria.'
                : 'Your salary slips will appear here once generated.'}
            </p>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="bg-white px-6 py-4 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex flex-1 justify-between sm:hidden">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="relative inline-flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page === pagination.totalPages}
                  className="relative ml-3 inline-flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-semibold">{(page - 1) * 10 + 1}</span> to{' '}
                    <span className="font-semibold">
                      {Math.min(page * 10, pagination.totalRecords)}
                    </span>{' '}
                    of <span className="font-semibold">{pagination.totalRecords}</span> results
                  </p>
                </div>
                <div>
                  <nav className="isolate inline-flex -space-x-px rounded-lg shadow-sm" aria-label="Pagination">
                    <button
                      onClick={() => setPage(page - 1)}
                      disabled={page === 1}
                      className="relative inline-flex items-center rounded-l-lg px-3 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 transition-colors"
                    >
                      Previous
                    </button>
                    {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((pageNumber) => (
                      <button
                        key={pageNumber}
                        onClick={() => setPage(pageNumber)}
                        className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                          pageNumber === page
                            ? 'z-10 bg-blue-600 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                            : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 transition-colors'
                        }`}
                      >
                        {pageNumber}
                      </button>
                    ))}
                    <button
                      onClick={() => setPage(page + 1)}
                      disabled={page === pagination.totalPages}
                      className="relative inline-flex items-center rounded-r-lg px-3 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 transition-colors"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Salary Slip View Modal */}
        <Modal
          isOpen={showViewModal}
          onClose={() => {
            setShowViewModal(false);
            setSelectedPayrollId(null);
          }}
          title="Salary Slip Details"
          size="lg"
        >
          {isLoadingSalarySlip ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : salarySlipError ? (
            <ErrorMessage message="Failed to load salary slip details" />
          ) : salarySlipData ? (
            <div className="space-y-6">
              {/* Employee Info */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <DocumentTextIcon className="h-5 w-5 mr-2 text-blue-600" />
                  Employee Information
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Employee ID:</span>
                    <span className="text-gray-900">{salarySlipData.data.employee.employeeId}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Name:</span>
                    <span className="text-gray-900">{salarySlipData.data.employee.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Department:</span>
                    <span className="text-gray-900">{salarySlipData.data.employee.department}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Designation:</span>
                    <span className="text-gray-900">{salarySlipData.data.employee.designation}</span>
                  </div>
                </div>
              </div>

              {/* Salary Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Earnings */}
                <div className="bg-white p-6 rounded-xl border border-gray-200">
                  <h4 className="text-md font-semibold text-green-600 mb-4 flex items-center">
                    <CurrencyRupeeIcon className="h-5 w-5 mr-2" />
                    Earnings
                  </h4>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between py-2">
                      <span className="text-gray-600">Basic Salary</span>
                      <span className="font-semibold text-gray-900">₹{salarySlipData.data.payrollDetails.basicSalary?.toLocaleString('en-IN') || 0}</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-gray-600">HRA</span>
                      <span className="font-semibold text-gray-900">₹{salarySlipData.data.payrollDetails.hra?.toLocaleString('en-IN') || 0}</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-gray-600">Allowances</span>
                      <span className="font-semibold text-gray-900">₹{salarySlipData.data.payrollDetails.totalAllowances?.toLocaleString('en-IN') || 0}</span>
                    </div>
                    <div className="flex justify-between py-3 pt-4 border-t border-gray-200 font-semibold">
                      <span className="text-gray-900">Gross Earnings</span>
                      <span className="text-green-600">₹{salarySlipData.data.payrollDetails.grossEarnings?.toLocaleString('en-IN') || 0}</span>
                    </div>
                  </div>
                </div>

                {/* Deductions */}
                <div className="bg-white p-6 rounded-xl border border-gray-200">
                  <h4 className="text-md font-semibold text-red-600 mb-4 flex items-center">
                    <XMarkIcon className="h-5 w-5 mr-2" />
                    Deductions
                  </h4>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between py-2">
                      <span className="text-gray-600">PF (Employee)</span>
                      <span className="font-semibold text-gray-900">₹{salarySlipData.data.payrollDetails.pfEmployee?.toLocaleString('en-IN') || 0}</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-gray-600">ESI (Employee)</span>
                      <span className="font-semibold text-gray-900">₹{salarySlipData.data.payrollDetails.esiEmployee?.toLocaleString('en-IN') || 0}</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-gray-600">Professional Tax</span>
                      <span className="font-semibold text-gray-900">₹{salarySlipData.data.payrollDetails.professionalTax?.toLocaleString('en-IN') || 0}</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-gray-600">TDS</span>
                      <span className="font-semibold text-gray-900">₹{salarySlipData.data.payrollDetails.tds?.toLocaleString('en-IN') || 0}</span>
                    </div>
                    <div className="flex justify-between py-3 pt-4 border-t border-gray-200 font-semibold">
                      <span className="text-gray-900">Total Deductions</span>
                      <span className="text-red-600">₹{salarySlipData.data.payrollDetails.totalDeductions?.toLocaleString('en-IN') || 0}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Net Pay */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-xl border border-green-100 text-center">
                <div className="text-2xl font-bold text-green-600 mb-2">
                  Net Pay: ₹{salarySlipData.data.payrollDetails.netSalary?.toLocaleString('en-IN') || 0}
                </div>
                <p className="text-sm text-green-700">This is your take-home salary after all deductions</p>
              </div>

              {/* Download Button */}
              <div className="flex justify-end pt-4">
                <button
                  onClick={() => handleDownloadSalarySlip(salarySlipData.data.payrollId, salarySlipData.data.employee.employeeId, salarySlipData.data.month, salarySlipData.data.year)}
                  disabled={isDownloading}
                  className="inline-flex items-center px-6 py-3 border border-transparent rounded-lg text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm"
                >
                  <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                  {isDownloading ? 'Downloading...' : 'Download Salary Slip'}
                </button>
              </div>
            </div>
          ) : null}
        </Modal>
      </div>
    </div>
  );
};

export default SalarySlipDownload;